/** @jsxImportSource react */
'use client';

import { Suspense, useEffect } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { isNonProductionAnalyticsHost } from '@onark/shared/analyticsEnv';
import type { AnalyticsSurface } from '@onark/shared/analyticsSurface';
import { trackMetaSignupCtaClicked } from '@OnArk/utils/metaPixel';
import posthog from 'posthog-js';

// PostHog analytics — mirrors the product app's config so events from
// frontend/utils/posthog.ts land in the same project. This is critical for the
// marketing → product funnel to be a single continuous session.
//
// Env var name matches the product app: NEXT_PUBLIC_POSTHOG_API_KEY.
// person_profiles: 'always' creates anonymous profiles for ad-clickers so
// $initial_utm_* persists and merges into the identified user on signup.

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

function init() {
  if (initialized || typeof window === 'undefined') return;
  // Same runtime-host gate as utils/posthog.ts: the prod key is committed and
  // build-time-inlined, so preview builds and local dev carry it — only the
  // hostname distinguishes them from production. Keep the two gates identical
  // or QA on a preview marketing page writes into the prod project.
  if (
    process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_NONPROD !== 'true' &&
    isNonProductionAnalyticsHost(window.location.hostname)
  ) {
    return;
  }
  const key = process.env.NEXT_PUBLIC_POSTHOG_API_KEY;

  if (!key) return;
  posthog.init(key, {
    // First-party proxy (matches the product app) so content/ad blockers can't
    // drop events. Marketing is served behind onark.app, so /ingest resolves to
    // the app/ingest Route Handler → us.i.posthog.com. ui_host is the app UI.
    api_host: '/ingest',
    // we capture manually per route below
    capture_pageleave: true,
    capture_pageview: false,
    capture_performance: {
      network_timing: true,
      web_vitals: true,
    },
    loaded: () => {
      initialized = true;
    },
    person_profiles: 'always',
    ui_host: 'https://us.posthog.com',
  });
  initialized = true;
  // Everything this app serves is the marketing surface. Registering on every
  // full page load also overwrites a persisted `surface` left by the product
  // app (guest/admin) — super properties stick per-origin, and both apps share
  // onark.app. Contract: @onark/shared/analyticsSurface.
  const surface: AnalyticsSurface = 'marketing';

  posthog.register({ surface });
}

function PageviewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    init();
    if (!initialized || !process.env.NEXT_PUBLIC_POSTHOG_API_KEY) return;
    // ReadonlyURLSearchParams in this Next/React types combo doesn't expose
    // .size — use .toString() truthiness instead (empty params → empty string).
    const qs = searchParams?.toString() ?? '';
    const url = pathname + (qs ? `?${qs}` : '');

    // PostHog
    posthog.capture('$pageview', {
      $current_url: window.location.origin + url,
    });

    // GA4 — page_view event mirrors the frontend's gtag('event', 'page_view') call.
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: url,
        page_title: document.title,
      });
    }

    // Meta Pixel — PageView fires on every route change so SPA navigation counts
    // the same as it does in the product app.
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [pathname, searchParams]);

  return null;
}

export function Analytics() {
  return (
    <Suspense fallback={null}>
      <PageviewTracker />
    </Suspense>
  );
}

/** Capture a custom marketing-funnel event in PostHog. No-op if PostHog isn't loaded. */
export function track(event: string, properties?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !initialized) return;
  posthog.capture(event, properties);
}

// Meta Pixel events are NOT re-implemented here. This module used to carry a
// `trackMetaLead` that claimed to match utils/metaPixel.ts and didn't: it fired
// the standard `Lead` on a mere CTA click. Marketing and product share one
// pixel id on purpose, so that drift fed the LEAD-optimized campaign a click as
// if it were a conversion. Import the shared helpers instead — one definition
// of what each funnel stage reports.

/** GA4 conversion event — companion to the Meta CTA-click event. */
function trackGaSignupClick(label = 'Sign up') {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function')
    return;
  window.gtag('event', 'sign_up_click', {
    event_category: 'engagement',
    event_label: label,
  });
}

/** Fire all signup-funnel events from a CTA click handler in one call. */
export function trackSignupClick(source: string) {
  track('signup_cta_clicked', { source });
  // Custom event, never standard `Lead` — see trackMetaSignupCtaClicked.
  trackMetaSignupCtaClicked(source);
  trackGaSignupClick(source);
}
